import { FileText, Landmark, Sparkles } from "lucide-react";
import { DotChip } from "@/components/shared/DotChip";
import { makeTr, type LandingLocale, localePath } from "@/lib/landing/locale";
import { ContourField, Container, CtaAnchor, CtaLink, Glow } from "./primitives";
import { StatusBar } from "./mock";

/**
 * The matching moment, the product in one picture: a receipt as the AI read
 * it and a Fio payment, slightly tilted like cards on a desk, joined by a
 * glowing contour line with a check at its middle. Static: it shows in its
 * matched state with the first paint, together with the headline.
 * Invented supplier, IČO and amount.
 *
 * Geometry, in % of the figure: the receipt spans y 7–48 %, the payment
 * starts at y 68 %, and the line is a symmetric curve from (32, 46) to
 * (54, 70), so its midpoint, where the check sits, is exactly (43, 58).
 */
function MatchVisual({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const field = (label: string, value: string, mono = false) => (
    <div className="flex items-baseline justify-between gap-3 py-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono font-semibold tabular-nums" : "font-medium"}>{value}</dd>
    </div>
  );
  return (
    <figure className="relative mx-auto aspect-[10/11] w-full max-w-[40rem] sm:aspect-[6/5] lg:-mr-12 lg:w-[calc(100%+3rem)] lg:max-w-none">
      <figcaption className="sr-only">
        {tr(
          "Ukázka: účtenka z papírnictví, jak ji přečetla AI, a platba kartou z Fio účtu, které Zettl spároval. Vymyšlená data.",
          "Example: a stationery receipt as the AI read it and a card payment from a Fio account, matched by Zettl. Invented data."
        )}
      </figcaption>
      <div aria-hidden="true" className="absolute inset-0 text-[11px] sm:text-[13px]">
        {/* Ambient glow under the cards: action green in light, brand green in dark. */}
        <div
          className="absolute inset-0 [--glow:var(--action)] dark:[--glow:var(--primary)]"
          style={{
            background:
              "radial-gradient(closest-side at 45% 55%, hsl(var(--glow) / 0.26), hsl(var(--glow) / 0.07) 60%, transparent)",
          }}
        />

        {/* The connector, under the cards so its ends tuck beneath them. */}
        <svg
          className="absolute inset-0 h-full w-full [--line:var(--action)] dark:[--line:var(--primary)]"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          fill="none"
        >
          {[
            { w: 10, o: 0.18 },
            { w: 2, o: 1 },
          ].map(({ w, o }) => (
            <path
              key={w}
              d="M32 46 C 32 58, 54 58, 54 70"
              stroke="hsl(var(--line))"
              strokeOpacity={o}
              strokeWidth={w}
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>

        {/* The month: the dashboard's tile, clear of the receipt. Frosted glass,
            so it reads as a layer above the two solid cards: the contours show
            through it, blurred, under a sheen and a bright edge. */}
        <div className="absolute right-0 top-0 w-[36%] overflow-hidden rounded-xl border border-white/80 bg-gradient-to-br from-white/50 to-white/5 p-3 shadow-[inset_0_1px_0_rgb(255_255_255/0.95),inset_0_0_0_1px_rgb(255_255_255/0.25),0_12px_32px_-12px_rgb(0_0_0/0.2)] backdrop-blur-[3px] backdrop-saturate-150 dark:border-white/20 dark:from-white/[0.14] dark:to-white/[0.03] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.22),0_16px_40px_-16px_rgb(0_0_0/0.7)] sm:p-4">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-semibold tracking-tight sm:text-base">
              {tr("Říjen", "October")}
            </p>
            <p className="text-xs font-semibold tabular-nums sm:text-sm">95 %</p>
          </div>
          <p className="mt-0.5 tabular-nums text-muted-foreground">
            {tr("19 plateb · zbývá 1", "19 payments · 1 left")}
          </p>
          <StatusBar
            className="mt-2.5 h-1.5"
            counts={{ matched: 15, invoice: 2, notNeeded: 1, missing: 0, unmatched: 1 }}
          />
        </div>

        {/* The receipt, as it came in by e-mail and the AI read it. */}
        <div className="absolute left-0 top-[7%] flex h-[41%] w-[56%] -rotate-[3deg] flex-col justify-center rounded-xl border bg-card px-3 shadow-xl sm:px-5">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 whitespace-nowrap font-semibold">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-[hsl(var(--row-red))] text-[hsl(var(--dot-red))] sm:size-7">
                <FileText className="size-3.5" strokeWidth={2} />
              </span>
              {tr("Účtenka", "Receipt")}
            </span>
            <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-[hsl(var(--action))] dark:text-primary sm:text-[11px]">
              <Sparkles className="size-3" />
              {tr("Přečteno AI", "Read by AI")}
            </span>
          </div>
          <dl className="mt-2 divide-y">
            {field(tr("Dodavatel", "Supplier"), "Papírnictví Kotva")}
            {field("IČO", "87654321", true)}
            {field(tr("Datum", "Date"), "12. 10. 2026", true)}
            {field(tr("Částka", "Amount"), "1 249,00 Kč", true)}
          </dl>
        </div>

        {/* The Fio payment: the row of the month view. */}
        <div className="absolute right-0 top-[68%] w-[62%]">
          <div className="rotate-[2deg] rounded-xl border bg-card p-3 shadow-xl sm:p-4">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Landmark className="size-3.5" strokeWidth={2} />
              <span>{tr("Fio · korunový účet", "Fio · CZK account")}</span>
            </div>
            <div className="mt-2.5 rounded-lg border bg-background/60 px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-medium sm:text-sm">Papírnictví Kotva</p>
                <p className="whitespace-nowrap font-mono tabular-nums sm:text-sm">−1 249,00 Kč</p>
              </div>
              <div className="mt-1.5 flex items-center gap-3">
                <span className="tabular-nums text-muted-foreground">
                  {tr("Karta · 12. 10.", "Card · 12 Oct")}
                </span>
                <DotChip color="green" className="text-foreground">
                  {tr("Spárováno", "Matched")}
                </DotChip>
              </div>
            </div>
          </div>
        </div>

        {/* The check at the line's midpoint. */}
        <div className="absolute left-[43%] top-[58%] -translate-x-1/2 -translate-y-1/2">
          <div className="flex size-8 items-center justify-center rounded-full bg-[hsl(var(--action))] text-white shadow-[0_0_0_6px_hsl(var(--primary)/0.18),0_0_24px_hsl(var(--primary)/0.6)] dark:bg-primary dark:text-primary-foreground sm:size-9">
            <svg viewBox="0 0 16 16" className="size-4" fill="none">
              <path
                d="M3.5 8.5 6.5 11.5 12.5 5"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </figure>
  );
}

/**
 * Hero: the h1 (the page's LCP), the definition sentence, two buttons and the
 * matching visual. It fills the first screen below the sticky nav (h-16 and its 1px border) with
 * its content centred, so the next section starts on scroll.
 */
export function LandingHero({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);

  return (
    <section
      aria-labelledby="hero-title"
      className="relative isolate flex min-h-[calc(100svh-4rem-1px)] items-center overflow-hidden"
    >
      <Glow at="top" />
      <ContourField variant="hero" />
      <Container className="grid items-center gap-12 py-12 sm:py-16 lg:grid-cols-[1fr_1.1fr] lg:gap-12">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full border bg-background/80 px-3 py-1 text-[13px] font-medium">
            <span
              aria-hidden="true"
              className="size-[7px] rounded-full bg-[hsl(var(--dot-green))] shadow-[0_0_8px_hsl(var(--dot-green))]"
            />
            {tr("Pro klienty Fio banky", "For Fio banka customers")}
          </p>
          <h1
            id="hero-title"
            className="mt-6 text-[2.5rem] font-semibold leading-[1.04] tracking-tight sm:text-6xl xl:text-[3.6rem]"
          >
            {/* Honest promise: Zettl looks for a document for every payment and
                matches what it can; it cannot match a document nobody sent. */}
            {tr(
              "Platby z Fio banky a doklady k nim ",
              "Your Fio banka payments and their documents, "
            )}
            <span className="text-[hsl(var(--action))] dark:text-primary">
              {tr("na jednom místě.", "in one place.")}
            </span>
          </h1>
          {/* The definition sentence: plain text, so answer engines can quote it. */}
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground">
            {tr(
              "Zettl je online aplikace pro s.r.o. a OSVČ s účtem u Fio banky: přes Fio API stahuje platby z vašeho účtu a ke každé automaticky hledá účtenku nebo fakturu.",
              "Zettl is a web app for Czech companies and freelancers (OSVČ) banking with Fio banka: it pulls payments through the Fio API and automatically looks for each one's receipt or invoice."
            )}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CtaLink to={localePath(locale, "/auth?tab=register")} arrow>
              {tr("Vyzkoušet 14 dní zdarma", "Try it free for 14 days")}
            </CtaLink>
            <CtaAnchor href="#jak-to-funguje">{tr("Jak to funguje", "How it works")}</CtaAnchor>
          </div>
        </div>

        <MatchVisual locale={locale} />
      </Container>
    </section>
  );
}
